// Per-medication amount control for batch quick-log and the suggestion card
// (MEDICATION_BATCH_DOSE_ADJUST_PLAN.md D-2, D-6). `full` is the Review & adjust
// row; `compact` is the chip + drawer. Combo products are a lock row (D-5).

import { useState } from "react";
import {
  MAX_DOSE_MULTIPLE,
  amountToDose,
  clampAmount,
  doseControlKind,
  doseToAmount,
  formatAmount,
  isAdjusted,
  isFormUnit,
  pillCountForDose,
  stepFor,
  usualDose,
  type DoseControlItem,
} from "@medassist/shared";

export interface DoseAdjustRowProps {
  item: DoseControlItem & { name: string; strength: string };
  /** Current total dose in item.unit. */
  dose: number;
  /** Called with an already clamped dose. */
  onChange: (dose: number) => void;
  density: "full" | "compact";
  /** Overdose red flag from the picker. */
  flagged?: boolean;
  disabled?: boolean;
  /** Compact density: drawer open. */
  open?: boolean;
  onToggleOpen?: () => void;
}

/** Small spinner copied from MedPickerModal (the safety-check affordance). */
export function DoseCheckSpinner({
  className = "h-4 w-4",
  onAccent = false,
}: {
  className?: string;
  /** Light ring for use on a filled accent button. */
  onAccent?: boolean;
}) {
  return (
    <div
      className={
        "animate-spin rounded-full border-2 " +
        (onAccent
          ? "border-white/40 border-t-white "
          : "border-gray-300 border-t-(--accent) dark:border-gray-700 ") +
        className
      }
      aria-hidden="true"
    />
  );
}

function formWord(item: DoseControlItem): string {
  return item.pillForm ?? (isFormUnit(item.unit) ? item.unit : "tablet");
}

function pluralWord(word: string): string {
  if (/(?:s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  return `${word}s`;
}

function trimNumber(n: number): string {
  return String(Math.round(n * 100) / 100);
}

function maxAmount(item: DoseControlItem): number {
  return MAX_DOSE_MULTIPLE * doseToAmount(item, usualDose(item));
}

/** "Max 8 per dose" — the number is the control amount (pills, mL, …). */
function maxCaption(item: DoseControlItem): string {
  const label = formatAmount(item, maxAmount(item));
  const n = label.split(" ")[0] ?? trimNumber(maxAmount(item));
  return `Max ${n} per dose`;
}

function usualCaption(item: DoseControlItem): string {
  const usual = usualDose(item);
  const amount = doseToAmount(item, usual);
  if (doseControlKind(item) === "pill" && item.unit) {
    return `${formatAmount(item, amount)} (${trimNumber(usual)} ${item.unit})`;
  }
  return formatAmount(item, amount);
}

/** Bound a typed dose (dose units, not pill count). Exact-amount entry does not snap to a step. */
function clampTypedDose(item: DoseControlItem, raw: number): number {
  const max = MAX_DOSE_MULTIPLE * usualDose(item);
  const capped = Math.min(raw, max);
  return Math.round(capped * 100) / 100;
}

const STEP_BTN =
  "relative inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border border-gray-300 text-xl leading-none text-gray-700 select-none hover:bg-gray-100 disabled:cursor-default disabled:opacity-35 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 [@media(pointer:coarse)]:after:absolute [@media(pointer:coarse)]:after:-inset-1 [@media(pointer:coarse)]:after:content-['']";

export function DoseAdjustRow({
  item,
  dose,
  onChange,
  density,
  flagged = false,
  disabled = false,
  open = false,
  onToggleOpen,
}: DoseAdjustRowProps) {
  const kind = doseControlKind(item);
  const [exact, setExact] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [fieldMsg, setFieldMsg] = useState<string | null>(null);

  const safeDose = Number.isFinite(dose) && dose > 0 ? dose : usualDose(item);
  const changed = isAdjusted(item, safeDose);
  const amount = doseToAmount(item, safeDose);
  const step = stepFor(item);
  const showExact = kind === "pill" && exact;

  function emit(next: number) {
    if (disabled) return;
    if (Math.abs(next - safeDose) > 1e-9) onChange(next);
  }

  function nudge(dir: 1 | -1) {
    setDraft(null);
    setFieldMsg(null);
    const nextAmt = clampAmount(item, amount + dir * step);
    emit(amountToDose(item, nextAmt));
  }

  const atMin = clampAmount(item, amount - step) >= amount - 1e-9;
  const atMax = clampAmount(item, amount + step) <= amount + 1e-9;

  function onType(raw: string) {
    setDraft(raw);
    const v = parseFloat(raw);
    if (!Number.isFinite(v) || v <= 0) {
      setFieldMsg("Enter an amount above 0");
      return;
    }
    const max = MAX_DOSE_MULTIPLE * usualDose(item);
    if (v > max + 1e-9) setFieldMsg(maxCaption(item));
    else setFieldMsg(null);
    emit(clampTypedDose(item, v));
  }

  function onBlurField() {
    setDraft(null);
    setFieldMsg(null);
  }

  function enterExact() {
    setExact(true);
    setDraft(trimNumber(safeDose));
    setFieldMsg(null);
  }

  function leaveExact() {
    const count = pillCountForDose(item, safeDose);
    const rounded = amountToDose(item, count);
    setExact(false);
    setDraft(null);
    setFieldMsg(null);
    emit(rounded);
  }

  function reset() {
    setExact(false);
    setDraft(null);
    setFieldMsg(null);
    emit(usualDose(item));
  }

  const captions: string[] = [];
  if ((kind === "pill" || kind === "form") && !showExact) {
    if (step === 1) captions.push(`Whole ${pluralWord(formWord(item))} only - change in Medications`);
    else if (step === 0.5) captions.push("½ steps allowed");
    else if (step === 0.25) captions.push("¼ steps allowed");
  }
  if (atMax && !showExact && kind !== "combo") captions.push(maxCaption(item));

  function amountControl() {
    if (showExact || kind === "free") {
      const unit = item.unit ?? "";
      return (
        <span className="inline-flex items-center gap-1.5">
          {kind === "free" && (
            <button
              type="button"
              onClick={() => nudge(-1)}
              disabled={disabled || atMin}
              className={STEP_BTN}
              aria-label={`Decrease ${item.name}`}
            >
              −
            </button>
          )}
          <input
            type="number"
            inputMode="decimal"
            value={draft ?? trimNumber(safeDose)}
            min={0}
            step={kind === "free" ? step : "any"}
            disabled={disabled}
            onChange={(e) => onType(e.target.value)}
            onBlur={onBlurField}
            aria-label={`${item.name} amount${unit ? ` in ${unit}` : ""}`}
            aria-invalid={fieldMsg ? true : undefined}
            className="w-20 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-right text-sm font-semibold text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          {unit && <span className="text-sm text-gray-500 dark:text-gray-400">{unit}</span>}
          {kind === "free" && (
            <button
              type="button"
              onClick={() => nudge(1)}
              disabled={disabled || atMax}
              className={STEP_BTN}
              aria-label={`Increase ${item.name}`}
            >
              +
            </button>
          )}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1" role="group" aria-label={`${item.name} amount`}>
        <button
          type="button"
          onClick={() => nudge(-1)}
          disabled={disabled || atMin}
          className={STEP_BTN}
          aria-label={`Decrease ${item.name}`}
        >
          −
        </button>
        <span className="min-w-24 text-center text-base font-bold text-gray-900 tabular-nums dark:text-gray-100">
          {formatAmount(item, amount)}
          {kind === "pill" && item.unit && (
            <small className="block text-[11px] font-normal text-gray-500 dark:text-gray-400">
              = {trimNumber(amountToDose(item, amount))} {item.unit}
            </small>
          )}
        </span>
        <button
          type="button"
          onClick={() => nudge(1)}
          disabled={disabled || atMax}
          className={STEP_BTN}
          aria-label={`Increase ${item.name}`}
        >
          +
        </button>
      </span>
    );
  }

  function subline() {
    const bits = changed || kind === "pill" || captions.length > 0 || fieldMsg;
    if (!bits) return null;
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        {changed && (
          <>
            <span>usual: {usualCaption(item)}</span>
            <button
              type="button"
              onClick={reset}
              disabled={disabled}
              className="text-(--accent) underline disabled:opacity-50"
            >
              Reset
            </button>
          </>
        )}
        {kind === "pill" &&
          (showExact ? (
            <button
              type="button"
              onClick={leaveExact}
              disabled={disabled}
              className="text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-300"
            >
              Use {formWord(item)} count instead
            </button>
          ) : (
            <button
              type="button"
              onClick={enterExact}
              disabled={disabled}
              className="text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-300"
            >
              Enter exact amount
            </button>
          ))}
        {captions.map((c) => (
          <span key={c}>{c}</span>
        ))}
        {fieldMsg && <span className="text-red-600 dark:text-red-400">{fieldMsg}</span>}
      </div>
    );
  }

  if (kind === "combo") {
    if (density === "compact") {
      return <span className="text-xs text-gray-500 dark:text-gray-400">🔒 Fixed combination</span>;
    }
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-800 dark:bg-gray-900">
        <div className="font-semibold text-gray-900 dark:text-gray-100">{item.name}</div>
        {item.strength && <div className="text-xs text-gray-500 dark:text-gray-400">{item.strength}</div>}
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span aria-hidden>🔒</span>
          Fixed combination - amount can&apos;t be changed here
        </div>
      </div>
    );
  }

  if (density === "compact") {
    const chipLabel = formatAmount(item, amount);
    return (
      <>
        <button
          type="button"
          onClick={onToggleOpen}
          disabled={disabled}
          aria-expanded={open}
          className={
            "order-2 inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold [@media(pointer:coarse)]:min-h-11 " +
            (changed
              ? "border-(--accent) text-(--accent)"
              : flagged
                ? "border-red-300 text-red-700 dark:border-red-700 dark:text-red-300"
                : "border-indigo-300 text-indigo-900 dark:border-indigo-700 dark:text-indigo-100")
          }
        >
          {chipLabel}
          {changed ? " - adjusted" : ""} ✎
        </button>
        {open && (
          <div className="order-last basis-full w-full rounded-lg border border-gray-200 bg-white p-2.5 text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
            {amountControl()}
            {subline()}
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className={
        "rounded-xl border px-3 py-2.5 " +
        (flagged
          ? "border-red-300 dark:border-red-700 "
          : changed
            ? "border-(--accent) "
            : "border-gray-200 dark:border-gray-800 ") +
        (changed ? "bg-(--accent)/10 " : "bg-white dark:bg-gray-900 ")
      }
    >
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-gray-900 dark:text-gray-100">
            {item.name}
            {changed && (
              <span className="ml-1.5 inline-flex items-center rounded-full border border-(--accent) px-1.5 text-[11px] font-semibold text-(--accent) align-middle">
                ✎ Adjusted
              </span>
            )}
          </div>
          {item.strength && <div className="text-xs text-gray-500 dark:text-gray-400">{item.strength}</div>}
        </div>
        <div className="ml-auto">{amountControl()}</div>
      </div>
      {subline()}
    </div>
  );
}
